import React from 'react';
import { gql, useMutation } from '@apollo/client';
import {
  Button, Input, InputNumber, Modal, Radio, Select, Space, Switch, Typography
} from 'antd';
import {
  CustomFieldTypeEnum,
  FieldDataFragment,
  MergeItemPersonCustomFieldFragment,
  RemoveCustomFieldValueMutation,
  RemoveCustomFieldValueMutationVariables,
  UpdateSelectCustomFieldValueForRecord,
  UpsertCustomFieldValueMutation,
  UpsertCustomFieldValueMutationVariables,
} from '../../../../../../../../../gql/typings';
import { StateArray } from '../../../../../../../../util/StateArrayType';
import { useLocalization } from '../../../../../../../../util/useLocalization';
import { Locale } from '../../../../../../../../../localization/LocalizationKeys';
import { reactJoin } from '../../../../../../../../util/reactJoin';
import { MergeEventEmitter } from '../../../useEntityMergeEmitter';


type CustomFieldMergeField = NonNullable<MergeItemPersonCustomFieldFragment['field']> & {
  allowMultiple?: boolean | null;
  allowUserCreate?: boolean | null;
  options?: {
    nodes: Array<{
      value?: {
        id: number;
        value?: string | null;
      } | null;
    }>;
  } | null;
};

type CustomFieldMergeValue = FieldDataFragment;

type SelectionSource = 'loser'|'winner'|'custom';

const valuesToDisplay = (values: CustomFieldMergeValue[]) => values
  .map(v => v.value.value ?? '')
  .filter(Boolean);

const valuesToMutationInput = (values: CustomFieldMergeValue[]): UpdateSelectCustomFieldValueForRecord[] => values
  .map(v => ({ valueId: v.value.id }));

const displayValues = (values: string[]) => `[${values.join(', ')}]`;

const isOptionType = (fieldType: CustomFieldTypeEnum) => (
  fieldType === CustomFieldTypeEnum.SELECT || fieldType === CustomFieldTypeEnum.CHECK
);

const isMultiValue = (field: CustomFieldMergeField) => (
  field.fieldTypeEnum === CustomFieldTypeEnum.TAG
  || field.fieldTypeEnum === CustomFieldTypeEnum.CHECK
  || !!field.allowMultiple
);

const uniqueValueIds = (values: number[]) => Array.from(new Set(values));

const buildOptionInput = (ids: number[]): UpdateSelectCustomFieldValueForRecord[] => uniqueValueIds(ids)
  .map(valueId => ({ valueId }));

const mergeUniqueValues = (values: CustomFieldMergeValue[]) => {
  const seen = new Set<number>();
  return values.filter(value => {
    if (seen.has(value.value.id)) return false;
    seen.add(value.value.id);
    return true;
  });
};

const uniqueTextValues = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

const buildTagInput = (
  values: string[],
  knownValues: CustomFieldMergeValue[],
): UpdateSelectCustomFieldValueForRecord[] => {
  const knownByText = new Map(knownValues.map(v => [v.value.value, v.value.id]));
  return values
    .filter(Boolean)
    .map(value => {
      const valueId = knownByText.get(value);
      return valueId ? { valueId } : { newValue: value };
    });
};

type AdminMergeItemCustomFieldModalProps = {
  visibleState: StateArray<boolean>;
  hasConflict: boolean;
  loserValues: CustomFieldMergeValue[];
  winnerValues: CustomFieldMergeValue[];
  field: CustomFieldMergeField;
  recordId: number;
  emitter: MergeEventEmitter['$'];
  emitterKey: string;
  onResolved: (displayValue: string) => void;
};

const AdminMergeItemCustomFieldModal: React.FC<AdminMergeItemCustomFieldModalProps> = ({
  visibleState,
  hasConflict,
  loserValues,
  winnerValues,
  field,
  recordId,
  emitter,
  emitterKey,
  onResolved,
}) => {
  const localization = useLocalization();
  const [upsert] = useMutation<UpsertCustomFieldValueMutation, UpsertCustomFieldValueMutationVariables>(UPSERT_MUTATION);
  const [removeCustomFieldValues] = useMutation<
  RemoveCustomFieldValueMutation,
  RemoveCustomFieldValueMutationVariables
  >(REMOVE_MUTATION);
  const [selectionSource, setSelectionSource] = React.useState<SelectionSource>('winner');
  const [textValue, setTextValue] = React.useState(winnerValues[0]?.value.value ?? loserValues[0]?.value.value ?? '');
  const [numberValue, setNumberValue] = React.useState<number | null>(
    parseInt(winnerValues[0]?.value.value ?? loserValues[0]?.value.value ?? '', 10) || null
  );
  const [switchValue, setSwitchValue] = React.useState(
    (winnerValues[0]?.value.value ?? loserValues[0]?.value.value) === 'true'
  );
  const mergedValues = React.useMemo(
    () => mergeUniqueValues([...winnerValues, ...loserValues]),
    [loserValues, winnerValues]
  );
  const [selectedIds, setSelectedIds] = React.useState<number[]>(mergedValues.map(v => v.value.id));
  const [tagValues, setTagValues] = React.useState<string[]>(uniqueTextValues(valuesToDisplay(mergedValues)));

  React.useEffect(() => {
    const merged = mergeUniqueValues([...winnerValues, ...loserValues]);
    setTextValue(winnerValues[0]?.value.value ?? loserValues[0]?.value.value ?? '');
    setNumberValue(parseInt(winnerValues[0]?.value.value ?? loserValues[0]?.value.value ?? '', 10) || null);
    setSwitchValue((winnerValues[0]?.value.value ?? loserValues[0]?.value.value) === 'true');
    setSelectedIds(merged.map(v => v.value.id));
    setTagValues(uniqueTextValues(valuesToDisplay(merged)));
    setSelectionSource('winner');
  }, [loserValues, winnerValues]);

  const loserDisplay = valuesToDisplay(loserValues);
  const winnerDisplay = valuesToDisplay(winnerValues);
  const allKnownValues = mergedValues;
  const showCustomOption = field.fieldTypeEnum === CustomFieldTypeEnum.TEXT
    || field.fieldTypeEnum === CustomFieldTypeEnum.NUMBER
    || field.fieldTypeEnum === CustomFieldTypeEnum.TAG
    || (field.fieldTypeEnum === CustomFieldTypeEnum.SELECT && !!field.allowMultiple);

  const buildCustomValue = (): {
    display: string;
    customValues: UpdateSelectCustomFieldValueForRecord[];
  } => {
    if (selectionSource === 'loser') return {
      display: displayValues(loserDisplay),
      customValues: valuesToMutationInput(loserValues),
    };
    if (selectionSource === 'winner') return {
      display: displayValues(winnerDisplay),
      customValues: valuesToMutationInput(winnerValues),
    };

    switch (field.fieldTypeEnum) {
      case CustomFieldTypeEnum.TEXT:
        return {
          display: textValue ?? '',
          customValues: [{ newValue: textValue ?? '' }],
        };
      case CustomFieldTypeEnum.NUMBER:
        return {
          display: numberValue?.toString() ?? '',
          customValues: [{ newValue: numberValue?.toString() ?? '' }],
        };
      case CustomFieldTypeEnum.SWITCH:
        return {
          display: switchValue ? 'true' : 'false',
          customValues: [{ newValue: switchValue ? 'true' : 'false' }],
        };
      case CustomFieldTypeEnum.TAG:
        return {
          display: displayValues(tagValues),
          customValues: buildTagInput(tagValues, allKnownValues),
        };
      case CustomFieldTypeEnum.SELECT:
      case CustomFieldTypeEnum.CHECK:
        return {
          display: displayValues(
            allKnownValues
              .filter(value => selectedIds.includes(value.value.id))
              .map(value => value.value.value ?? '')
              .filter(Boolean)
          ),
          customValues: buildOptionInput(selectedIds),
        };
      default:
        return { display: '', customValues: [] };
    }
  };

  const onChoose = () => {
    const { customValues, display } = buildCustomValue();
    const loserCustomLinkIds = loserValues.map(value => value.affiliation.id);
    onResolved(display);
    emitter.emit({
      key: emitterKey,
      state: 'finished',
      _source: 'AdminMergeItemCustomFieldModal:onChoose',
      onSave: async () => {
        await upsert({
          variables: {
            recordId,
            customFieldCode: field.code,
            customValues,
          },
        });
        if (loserCustomLinkIds.length > 0) {
          await removeCustomFieldValues({ variables: { customValues: loserCustomLinkIds } });
        }
      },
    });
    visibleState[1](false);
  };

  const renderCustomInput = () => {
    if (selectionSource !== 'custom') return null;
    if (field.fieldTypeEnum === CustomFieldTypeEnum.TEXT) return (
      <Input value={textValue ?? ''} onChange={e => setTextValue(e.target.value)} />
    );
    if (field.fieldTypeEnum === CustomFieldTypeEnum.NUMBER) return (
      <InputNumber value={numberValue} onChange={value => setNumberValue(value)} style={{ width: '100%' }} />
    );
    if (field.fieldTypeEnum === CustomFieldTypeEnum.SWITCH) return (
      <Switch checked={switchValue} onChange={setSwitchValue} />
    );
    if (field.fieldTypeEnum === CustomFieldTypeEnum.TAG) return (
      <Select
        mode="tags"
        value={tagValues}
        onChange={setTagValues}
        style={{ width: '100%' }}
        options={allKnownValues.map(v => ({
          value: v.value.value ?? '',
          label: v.value.value ?? '',
        }))}
      />
    );
    if (isOptionType(field.fieldTypeEnum)) return (
      <Select
        mode={isMultiValue(field) ? 'multiple' : undefined}
        value={isMultiValue(field) ? selectedIds : selectedIds[0]}
        onChange={newValue => setSelectedIds(Array.isArray(newValue) ? newValue : [newValue])}
        style={{ width: '100%' }}
        options={allKnownValues.map(value => ({
          value: value.value.id,
          label: value.value.value,
        }))}
      />
    );
    return null;
  };

  return (
    <Modal
      open={visibleState[0]}
      onCancel={() => visibleState[1](false)}
      footer={false}
    >
      <Space direction="vertical">
        {field.fieldTypeEnum}
        <span>
          {hasConflict && localization.formatMessage(Locale.Text.Merge_request_custom_field_conflict_modal_description, {
            field: <strong>{field.heading}</strong>,
            values1: <>[{reactJoin(loserDisplay.map((v, i) => <strong key={i}>{v}</strong>))}]</>,
            values2: <>[{reactJoin(winnerDisplay.map((v, i) => <strong key={i}>{v}</strong>))}]</>,
          })}
          {!hasConflict && <i>Some description...</i>}
        </span>

        <Radio.Group
          value={selectionSource}
          onChange={e => setSelectionSource(e.target.value)}
        >
          <Space direction="vertical">
            <Radio value="winner">
              Keep winner values: <Typography.Text strong>{displayValues(winnerDisplay)}</Typography.Text>
            </Radio>
            <Radio value="loser">
              Keep loser values: <Typography.Text strong>{displayValues(loserDisplay)}</Typography.Text>
            </Radio>
            {showCustomOption && (
              <Radio value="custom">
                Choose custom final values
              </Radio>
            )}
          </Space>
        </Radio.Group>

        {renderCustomInput()}

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
          <Button onClick={() => visibleState[1](false)}>
            {localization.formatMessage(Locale.Command.Cancel)}
          </Button>
          <Button type="primary" onClick={onChoose}>
            {localization.formatMessage(Locale.Command.Choose)}
          </Button>
        </div>
      </Space>
    </Modal>
  );
};

const UPSERT_MUTATION = gql`
  mutation UpsertCustomFieldValue(
    $customFieldCode: String!,
    $customValues: [UpdateSelectCustomFieldValueForRecord!]!,
    $recordId: Int!
  ) {
    setCustomLinkValues(customFieldCode: $customFieldCode, customValues: $customValues, recordId: $recordId) {
      customFieldCode
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation RemoveCustomFieldValue($customValues: [ID!]!) {
    removeCustomLinkValues(customLinkValueIds: $customValues)
  }
`;

export default AdminMergeItemCustomFieldModal;
